import { useRouter } from "@tanstack/react-router"
import { type ReactNode, useState } from "react"

import { confirmPasswordReset } from "../../api/auth-reset.api"
import { isApiProblem } from "../../api/problem"
import { Button } from "../../ui/button"
import { TextField } from "../../ui/field"
import {
  allRulesMet,
  checkableRules,
  IDENTIFIER_NOTE,
  RESET_DONE_CONSEQUENCES,
} from "./password-rules"
import styles from "./auth-screen.module.css"

/**
 * 1-Э1.3 «Новый пароль» — /reset-password?token=…
 *
 * ТРЕБОВАНИЯ ВИДНЫ ДО ВВОДА. Правило, показанное после отказа, читается как придирка:
 * человек придумал пароль, ввёл дважды и услышал «нет». Правило, показанное сразу,
 * — это условие задачи, и список отмечает выполненное по мере набора.
 *
 * УСПЕХ НАЗЫВАЕТ ДВА ПОСЛЕДСТВИЯ: вышли все устройства и сутки закрыта выгрузка базы.
 * Оба человек иначе обнаружит сам — в худший момент, с телефоном в руке у стойки.
 *
 * «ССЫЛКА НЕ ПОДОШЛА» — ОТДЕЛЬНОЕ СОСТОЯНИЕ С ДЕЙСТВИЕМ. Просроченная, использованная
 * и выдуманная ссылки неразличимы намеренно (по ответу нельзя понять, была ли она
 * настоящей), поэтому единственный полезный ответ — «запросить новую».
 */
export function ResetPasswordScreen(props: { readonly token?: string | undefined }): ReactNode {
  const router = useRouter()
  const [password, setPassword] = useState("")
  const [repeat, setRepeat] = useState("")
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [linkDead, setLinkDead] = useState(false)

  const token = props.token ?? ""
  const rulesOk = allRulesMet(password, "")
  const sameTwice = password.length > 0 && password === repeat

  const submit = async (): Promise<void> => {
    setBusy(true)
    setFailure(null)
    try {
      await confirmPasswordReset(token, password)
      setDone(true)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      // invalid_credentials на этом экране означает ровно одно: ссылка не подошла.
      // Пароль здесь ни при чём, и говорить про «проверьте адрес и пароль» нельзя.
      if (error.code === "invalid_credentials") {
        setLinkDead(true)
      } else if (error.code === "offline" || error.code === "timeout") {
        // ОБРЫВ СВЯЗИ НА ЭТОМ ЭКРАНЕ ОСОБЕННО ДОРОГ: человек не знает, сменился пароль
        // или нет, и любой общий текст он прочитает как «не сменился». Пока это
        // неизвестно, единственный честный совет — попробовать войти новым паролем.
        setFailure(
          "Связь прервалась. Неизвестно, успел ли пароль смениться: попробуйте войти новым — если не подойдёт, откройте ссылку из письма ещё раз.",
        )
      } else {
        setFailure(error.detail ?? error.title)
      }
    } finally {
      setBusy(false)
    }
  }

  if (token === "") {
    return (
      <div className={styles.screen}>
        <div className={styles.card}>
          <h1 className={styles.title}>Ссылка неполная</h1>
          <p className={styles.text}>
            В адресе нет кода восстановления — почтовые программы иногда рвут длинные
            ссылки на две строки. Откройте письмо и скопируйте ссылку целиком либо
            запросите новую.
          </p>
          <div className={styles.actions}>
            <Button
              variant="primary"
              onClick={() => {
                void router.navigate({ to: "/forgot-password" })
              }}
            >
              Запросить новую
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (linkDead) {
    return (
      <div className={styles.screen}>
        <div className={styles.card}>
          <h1 className={styles.title}>Ссылка не подошла</h1>
          {/*
            * ПРО «ПАРОЛЬ ОСТАЛСЯ ПРЕЖНИМ» ЗДЕСЬ ГОВОРИТЬ НЕЛЬЗЯ. Самый частый повод
            * увидеть этот экран — ссылка уже использована, то есть пароль как раз
            * СМЕНИЛСЯ, и обещание обратного отправляет человека вводить старый.
            */}
          <p className={styles.text}>
            Она уже использована или устарела: ссылка живёт 15 минут и открывается один
            раз. Если вы уже задали новый пароль по этой ссылке — входите с ним; если
            нет — запросите новую.
          </p>
          <div className={styles.actions}>
            <Button
              variant="primary"
              onClick={() => {
                void router.navigate({ to: "/forgot-password" })
              }}
            >
              Запросить новую
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                void router.navigate({ to: "/login" })
              }}
            >
              Ко входу
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className={styles.screen}>
        <div className={styles.card} role="status" aria-live="polite">
          <h1 className={styles.title}>Пароль изменён</h1>
          <ul className={styles.consequences}>
            {RESET_DONE_CONSEQUENCES.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <div className={styles.actions}>
            <Button
              variant="primary"
              onClick={() => {
                void router.navigate({ to: "/login" })
              }}
            >
              Войти с новым паролем
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <h1 className={styles.title}>Новый пароль</h1>

        <ul className={styles.rules}>
          {checkableRules("").map((rule) => {
            const met = rule.met(password, "")
            return (
              <li key={rule.key} className={met ? styles.ruleMet : styles.rule}>
                {met ? "✓" : "•"} {rule.text}
              </li>
            )
          })}
        </ul>
        {/*
          * Условие, которое экран проверить не может: почты человека он не знает —
          * тот пришёл по ссылке из письма. Галочка тут означала бы «проверено»,
          * а проверено не было.
          */}
        <p className={styles.hint}>{IDENTIFIER_NOTE}</p>

        <TextField
          label="Новый пароль"
          type="password"
          value={password}
          autoComplete="new-password"
          onChange={(event) => {
            setPassword(event.currentTarget.value)
          }}
        />
        <TextField
          label="Ещё раз"
          type="password"
          value={repeat}
          autoComplete="new-password"
          onChange={(event) => {
            setRepeat(event.currentTarget.value)
          }}
        />

        {repeat.length > 0 && !sameTwice ? (
          <p className={styles.hint}>Второй пароль отличается от первого.</p>
        ) : null}

        {failure === null ? null : (
          <p className={styles.failure} role="alert">
            {failure}
          </p>
        )}

        <div className={styles.actions}>
          <Button
            variant="primary"
            loading={busy}
            loadingLabel="Сохраняем…"
            deniedReason={
              !rulesOk
                ? "Пароль пока не отвечает требованиям выше"
                : sameTwice
                  ? undefined
                  : "Введите один и тот же пароль дважды"
            }
            onClick={() => {
              void submit()
            }}
          >
            Задать пароль
          </Button>
        </div>
      </div>
    </div>
  )
}
